import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";

const FAQS = [
  {
    q: "Is LIFTILE a manufacturer or a trading company?",
    a: "LIFTILE is a construction machinery manufacturer with its own manufacturing facility, engineering processes and quality inspections. We focus on designing and building reliable construction equipment using premium branded components.",
  },
  {
    q: "Can I visit your manufacturing facility?",
    a: "Yes. Customers, dealers and business partners are welcome to visit our manufacturing facility by scheduling an appointment with our team.",
  },
  {
    q: "Do you manufacture customized machinery?",
    a: "Yes. We can customize lifting height, capacity, configurations and other specifications based on project requirements.",
  },
  {
    q: "Do you provide installation and after-sales support?",
    a: "Yes. We provide installation guidance, technical assistance, spare parts support and dependable after-sales service to ensure smooth machine operation.",
  },
  {
    q: "How can I become a LIFTILE dealer or business partner?",
    a: "Simply contact our team through the Dealer or Contact page. We are continuously expanding our dealer and business partner network across India.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

/* =====================================================================
   ABOUT-PAGE FAQ — company-related questions only.
   Re-uses the premium accordion pattern from the home page but with
   different copy and a slightly more compact layout that fits the
   "ending" rhythm of the About page.
   ===================================================================== */
export default function AboutFAQSection() {
  return (
    <section
      data-testid="about-faq"
      className="relative bg-liftile-cream py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle blueprint */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      {/* Soft orange ambient */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full pointer-events-none opacity-[0.16]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-start">
          {/* Left header — sticky on desktop */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="lg:sticky lg:top-28"
          >
            <div className="heading-eyebrow mb-4 text-liftile-orange">
              Common Questions
            </div>
            <h2
              data-testid="about-faq-heading"
              className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
            >
              Still Have
              <br />
              <span className="text-liftile-orange">Questions?</span>
            </h2>
            <p className="font-body text-liftile-matte-black/70 mt-6 text-base md:text-lg leading-relaxed max-w-md">
              About our manufacturing, customization, partnerships and customer
              support — everything you might want to know about working with
              LIFTILE.
            </p>

            <Link
              to="/contact"
              data-testid="about-faq-talk-to-expert"
              className="group inline-flex items-center gap-3 mt-9 border-2 border-liftile-orange text-liftile-orange font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-3.5 rounded-sm bg-transparent transition-all duration-400 hover:bg-liftile-orange hover:text-white hover:-translate-y-0.5 hover:shadow-[0_15px_35px_-10px_rgba(255,107,26,0.6)]"
            >
              Talk to Our Team
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Accordion */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            transition={{ delay: 0.15 }}
          >
            <Accordion
              type="single"
              collapsible
              defaultValue="about-faq-0"
              className="space-y-3"
            >
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`about-faq-${i}`}
                  data-testid={`about-faq-item-${i}`}
                  className="group/item border-0 bg-white rounded-xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.12)] transition-all duration-400 data-[state=open]:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.28)] data-[state=open]:ring-1 data-[state=open]:ring-liftile-orange/60"
                >
                  <AccordionTrigger
                    data-testid={`about-faq-trigger-${i}`}
                    className="group/trigger w-full hover:no-underline [&>svg]:hidden px-6 md:px-7 py-5 md:py-6 text-left"
                  >
                    <div className="flex items-start gap-5 w-full">
                      <span className="shrink-0 mt-1 font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange font-semibold w-8 group-data-[state=open]/item:opacity-100 opacity-70 transition-opacity">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 font-heading text-base md:text-lg text-liftile-matte-black font-semibold leading-snug group-data-[state=open]/item:text-liftile-orange transition-colors duration-300">
                        {item.q}
                      </span>
                      <span
                        aria-hidden
                        className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-liftile-matte-black/10 text-liftile-matte-black/70 group-data-[state=open]/item:bg-liftile-orange group-data-[state=open]/item:text-white group-data-[state=open]/item:border-liftile-orange group-data-[state=open]/item:rotate-45 transition-all duration-400 ease-out"
                      >
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-6 md:px-7 pb-6 md:pb-7 pt-0">
                    <div style={{ paddingLeft: "3.25rem" }}>
                      <div className="h-px bg-liftile-orange/30 mb-4" />
                      <p className="font-body text-sm md:text-base text-liftile-matte-black/75 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
